import React, { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { LogOut, Upload } from "lucide-react";
import { API } from "@/lib/api";
import { COLOR_FIELDS } from "@/lib/constants";

export const OnboardingWizard = ({ initial, onDone, onLogout }) => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [seedDemo, setSeedDemo] = useState(true);
  const [form, setForm] = useState(() => ({
    company_name: initial?.company_name || "",
    owner_name: initial?.owner_name || "",
    logo_base64: initial?.logo_base64 || "",
    strasse: initial?.strasse || "",
    plz_ort: initial?.plz_ort || "",
    land: initial?.land || "Deutschland",
    telefon: initial?.telefon || "",
    email: initial?.email || "",
    website: initial?.website || "",
    ustid: initial?.ustid || "",
    color_primary: initial?.color_primary || "#2C3338",
    color_primary_dark: initial?.color_primary_dark || "#1B1F23",
    color_bg: initial?.color_bg || "#F4F5F6",
    color_border: initial?.color_border || "#DADEE2",
    color_ink: initial?.color_ink || "#1A1D21",
    disclaimer: initial?.disclaimer || "",
    customer_password: initial?.customer_password || "",
    // tax/bank
    steuernummer: "",
    finanzamt: "",
    iban: "",
    bic: "",
    bank: "",
    kontoinhaber: "",
  }));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setColor = (k, cssVar, v) => {
    set(k, v);
    if (typeof document !== "undefined") {
      document.documentElement.style.setProperty(cssVar, v);
    }
  };

  const onLogo = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte eine Bilddatei wählen");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo zu groß (max. 2 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => set("logo_base64", reader.result?.toString() || "");
    reader.readAsDataURL(file);
  };

  const steps = [
    { title: "Willkommen", sub: "Erzählen Sie uns von Ihrem Unternehmen" },
    { title: "Logo", sub: "Ihr Markenzeichen (optional)" },
    { title: "Adresse & Kontakt", sub: "Erscheint auf jedem Angebot" },
    { title: "Steuer & Bank", sub: "Für rechtskonforme Rechnungen" },
    { title: "Farben", sub: "Ihr individuelles Erscheinungsbild" },
    { title: "Zugang & Start", sub: "Letzte Schritte" },
  ];

  const next = () => setStep((s) => Math.min(steps.length - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const finish = async () => {
    if (!form.company_name.trim()) {
      toast.error("Bitte einen Firmennamen angeben");
      setStep(0);
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/settings/company`, {
        company_name: form.company_name,
        owner_name: form.owner_name,
        logo_base64: form.logo_base64,
        strasse: form.strasse,
        plz_ort: form.plz_ort,
        land: form.land,
        telefon: form.telefon,
        email: form.email,
        website: form.website,
        ustid: form.ustid,
        color_primary: form.color_primary,
        color_primary_dark: form.color_primary_dark,
        color_bg: form.color_bg,
        color_border: form.color_border,
        color_ink: form.color_ink,
        disclaimer: form.disclaimer,
        customer_password: form.customer_password,
      });
      await axios.post(`${API}/settings/steuerdaten`, {
        steuernummer: form.steuernummer,
        ustid: form.ustid,
        finanzamt: form.finanzamt,
        iban: form.iban,
        bic: form.bic,
        bank: form.bank,
        kontoinhaber: form.kontoinhaber || form.company_name,
      });
      if (seedDemo) {
        await axios.post(`${API}/onboarding/seed-demo`).catch(() => {});
      }
      await axios.post(`${API}/onboarding/complete`);
      toast.success("Einrichtung abgeschlossen!");
      await onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const fieldCls = "mt-1";

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] font-[Inter] py-8 px-4" data-testid="onboarding-wizard">
      <Toaster richColors position="top-right" />
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className="h-11 w-11 rounded-xl flex items-center justify-center text-white font-bold text-xl"
              style={{ background: "var(--brand-primary)" }}
            >
              {(form.company_name || "A").trim().charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-[Playfair_Display] text-lg font-bold text-[var(--brand-ink)]">
                Einrichtungs-Assistent
              </div>
              <div className="text-xs text-[var(--brand-muted)]">
                Schritt {step + 1} von {steps.length} · {steps[step].sub}
              </div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="onboarding-logout">
            <LogOut className="w-4 h-4 mr-1" /> Abmelden
          </Button>
        </div>

        {/* progress */}
        <div className="flex gap-1.5 mb-6">
          {steps.map((s, i) => (
            <div
              key={i}
              className="h-1.5 flex-1 rounded-full transition"
              style={{ background: i <= step ? "var(--brand-primary)" : "var(--brand-border)" }}
            />
          ))}
        </div>

        <Card className="p-6 md:p-8 bg-white border-[var(--brand-border)] shadow-lg">
          <h2 className="font-[Playfair_Display] text-2xl font-bold text-[var(--brand-ink)] mb-1">
            {steps[step].title}
          </h2>
          <p className="text-sm text-[var(--brand-muted)] mb-6">{steps[step].sub}</p>

          {step === 0 && (
            <div className="space-y-4">
              <div>
                <Label>Firmenname *</Label>
                <Input
                  className={fieldCls}
                  value={form.company_name}
                  onChange={(e) => set("company_name", e.target.value)}
                  placeholder="z. B. Muster Marketing GmbH"
                  data-testid="onboarding-company-name"
                  autoFocus
                />
              </div>
              <div>
                <Label>Inhaber / Ansprechpartner</Label>
                <Input
                  className={fieldCls}
                  value={form.owner_name}
                  onChange={(e) => set("owner_name", e.target.value)}
                  placeholder="z. B. Max Mustermann"
                  data-testid="onboarding-owner"
                />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center gap-5">
                <div className="h-28 w-28 rounded-xl border border-[var(--brand-border)] bg-[var(--brand-bg)] flex items-center justify-center overflow-hidden">
                  {form.logo_base64 ? (
                    <img src={form.logo_base64} alt="logo" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span className="text-3xl font-bold text-[var(--brand-primary)]">
                      {(form.company_name || "A").trim().charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--brand-primary)] text-white text-sm cursor-pointer hover:bg-[var(--brand-primary-dark)]">
                    <Upload className="w-4 h-4" /> Logo hochladen
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onLogo(e.target.files?.[0])}
                      data-testid="onboarding-logo-input"
                    />
                  </label>
                  {form.logo_base64 && (
                    <button
                      type="button"
                      className="block text-xs text-[var(--brand-muted)] underline"
                      onClick={() => set("logo_base64", "")}
                      data-testid="onboarding-logo-remove"
                    >
                      Entfernen
                    </button>
                  )}
                  <p className="text-xs text-[var(--brand-muted)] max-w-[200px]">
                    PNG/JPG, max. 2 MB. Ohne Logo nutzen wir Ihren Anfangsbuchstaben.
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <Label>Straße & Hausnr.</Label>
                <Input className={fieldCls} value={form.strasse} onChange={(e) => set("strasse", e.target.value)} data-testid="onboarding-strasse" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>PLZ & Ort</Label>
                  <Input className={fieldCls} value={form.plz_ort} onChange={(e) => set("plz_ort", e.target.value)} data-testid="onboarding-plzort" />
                </div>
                <div>
                  <Label>Land</Label>
                  <Input className={fieldCls} value={form.land} onChange={(e) => set("land", e.target.value)} data-testid="onboarding-land" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Telefon</Label>
                  <Input className={fieldCls} value={form.telefon} onChange={(e) => set("telefon", e.target.value)} data-testid="onboarding-telefon" />
                </div>
                <div>
                  <Label>E-Mail</Label>
                  <Input className={fieldCls} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="onboarding-email" />
                </div>
              </div>
              <div>
                <Label>Website</Label>
                <Input className={fieldCls} value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="www.beispiel.de" data-testid="onboarding-website" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Steuernummer</Label>
                  <Input className={fieldCls} value={form.steuernummer} onChange={(e) => set("steuernummer", e.target.value)} data-testid="onboarding-steuernummer" />
                </div>
                <div>
                  <Label>USt-IdNr.</Label>
                  <Input className={fieldCls} value={form.ustid} onChange={(e) => set("ustid", e.target.value)} placeholder="DE123456789" data-testid="onboarding-ustid" />
                </div>
              </div>
              <div>
                <Label>Finanzamt</Label>
                <Input className={fieldCls} value={form.finanzamt} onChange={(e) => set("finanzamt", e.target.value)} data-testid="onboarding-finanzamt" />
              </div>
              <Separator />
              <div>
                <Label>Kontoinhaber</Label>
                <Input className={fieldCls} value={form.kontoinhaber} onChange={(e) => set("kontoinhaber", e.target.value)} placeholder={form.company_name} data-testid="onboarding-kontoinhaber" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>IBAN</Label>
                  <Input className={fieldCls} value={form.iban} onChange={(e) => set("iban", e.target.value)} data-testid="onboarding-iban" />
                </div>
                <div>
                  <Label>BIC</Label>
                  <Input className={fieldCls} value={form.bic} onChange={(e) => set("bic", e.target.value)} data-testid="onboarding-bic" />
                </div>
              </div>
              <div>
                <Label>Bank</Label>
                <Input className={fieldCls} value={form.bank} onChange={(e) => set("bank", e.target.value)} data-testid="onboarding-bank" />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {COLOR_FIELDS.map((c) => (
                  <div key={c.key} className="flex items-center gap-3">
                    <input
                      type="color"
                      value={form[c.key]}
                      onChange={(e) => setColor(c.key, c.cssVar, e.target.value)}
                      className="h-10 w-12 rounded border border-[var(--brand-border)] cursor-pointer"
                      data-testid={`onboarding-${c.key.replace("_", "-")}`}
                    />
                    <div>
                      <div className="text-sm font-medium text-[var(--brand-ink)]">{c.label}</div>
                      <div className="text-xs text-[var(--brand-muted)] uppercase">{form[c.key]}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="rounded-lg border border-[var(--brand-border)] overflow-hidden">
                <div className="px-4 py-3" style={{ background: "var(--brand-bg)" }}>
                  <span className="text-xs uppercase tracking-widest" style={{ color: "var(--brand-primary)" }}>
                    Live-Vorschau
                  </span>
                  <div className="font-[Playfair_Display] font-bold text-lg" style={{ color: "var(--brand-ink)" }}>
                    {form.company_name || "Ihr Unternehmen"}
                  </div>
                </div>
                <div className="p-4 bg-white flex gap-2">
                  <span className="px-4 py-2 rounded-md text-white text-sm" style={{ background: "var(--brand-primary)" }}>
                    Button
                  </span>
                  <span className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}>
                    Akzent
                  </span>
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <div>
                <Label>Zugangs-Passwort für Kunden</Label>
                <Input
                  className={fieldCls}
                  value={form.customer_password}
                  onChange={(e) => set("customer_password", e.target.value)}
                  placeholder="z. B. Angebot2026"
                  data-testid="onboarding-customer-password"
                />
                <p className="text-xs text-[var(--brand-muted)] mt-1">
                  Kunden öffnen ihr Angebot über den Tracking-Link mit diesem Passwort.
                </p>
              </div>
              <div>
                <Label>Disclaimer / Hinweis (optional)</Label>
                <Textarea
                  className={fieldCls}
                  rows={3}
                  value={form.disclaimer}
                  onChange={(e) => set("disclaimer", e.target.value)}
                  placeholder="Erscheint als Hinweisblock im Angebot."
                  data-testid="onboarding-disclaimer"
                />
              </div>
              <label
                className="flex items-start gap-3 p-3 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-bg)] cursor-pointer"
                data-testid="onboarding-demo-toggle"
              >
                <input
                  type="checkbox"
                  checked={seedDemo}
                  onChange={(e) => setSeedDemo(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-[var(--brand-primary)]"
                  data-testid="onboarding-demo-checkbox"
                />
                <div>
                  <div className="text-sm font-semibold text-[var(--brand-ink)]">
                    Mit Beispieldaten starten
                  </div>
                  <div className="text-xs text-[var(--brand-muted)]">
                    Legt ein paar Demo-Angebote an, damit Sie direkt loslegen können.
                  </div>
                </div>
              </label>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-5 border-t border-[var(--brand-border)]">
            <Button
              variant="outline"
              onClick={back}
              disabled={step === 0 || saving}
              className="border-[var(--brand-border)]"
              data-testid="onboarding-back"
            >
              Zurück
            </Button>
            {step < steps.length - 1 ? (
              <Button
                onClick={next}
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
                data-testid="onboarding-next"
              >
                Weiter
              </Button>
            ) : (
              <Button
                onClick={finish}
                disabled={saving}
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
                data-testid="onboarding-finish"
              >
                {saving ? "Wird gespeichert…" : "Einrichtung abschließen"}
              </Button>
            )}
          </div>
        </Card>
        <div className="h-24" aria-hidden="true" />
      </div>
    </div>
  );
};
